import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Buildings, Warning, CheckCircle, CircleNotch, Info,
} from '@phosphor-icons/react';
import { fetchChambersPrefs, updateChambersPrefs } from '@/lib/meApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TimezoneSelect } from '@/components/ui/timezone-select';

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function PInput({
  value, onChange, placeholder, disabled, readOnly, type = 'text',
}: {
  value: string; onChange?: (v: string) => void; placeholder?: string;
  disabled?: boolean; readOnly?: boolean; type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      onChange={(e) => onChange?.(e.target.value)}
      className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-50 read-only:bg-muted/30"
    />
  );
}

export default function ChambersSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: chambers, isLoading } = useQuery({
    queryKey: ['me-chambers'],
    queryFn: fetchChambersPrefs,
    staleTime: 0,
  });

  // Editability is decided by the SERVER (docs/SETTINGS-MODEL.md §1.2) and arrives on the payload
  // as `canEdit`. The page must not re-derive it from a role list — that is exactly how a chambers
  // silk ended up able to rename the chambers and change its VAT number.
  const isAdmin = chambers?.canEdit ?? false;

  const [form, setForm] = useState({
    name: '', legalName: '', registrationNo: '', vatNumber: '',
    email: '', phone: '', website: '',
    addressLine1: '', addressLine2: '', city: '', county: '', postcode: '',
    timezone: '',
    fiscalYearStart: '',
  });
  const [saved, setSaved] = useState({ ...form });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!chambers) return;
    const initial = {
      name: chambers.name ?? '',
      legalName: chambers.legalName ?? '',
      registrationNo: chambers.registrationNo ?? '',
      vatNumber: chambers.vatNumber ?? '',
      email: chambers.email ?? '',
      phone: chambers.phone ?? '',
      website: chambers.website ?? '',
      addressLine1: chambers.addressLine1 ?? '',
      addressLine2: chambers.addressLine2 ?? '',
      city: chambers.city ?? '',
      county: chambers.county ?? '',
      postcode: chambers.postcode ?? '',
      timezone: chambers.timezone ?? 'Europe/London',
      fiscalYearStart: chambers.fiscalYearStart ?? '04-06',
    };
    setForm(initial);
    setSaved(initial);
  }, [chambers]);

  const isDirty = Object.keys(form).some((k) => form[k as keyof typeof form] !== saved[k as keyof typeof saved]);
  const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const handleSave = async () => {
    if (!isDirty || saving || !isAdmin) return;
    setSaving(true);
    try {
      const updated = await updateChambersPrefs(form);
      const next = {
        name: updated.name ?? '',
        legalName: updated.legalName ?? '',
        registrationNo: updated.registrationNo ?? '',
        vatNumber: updated.vatNumber ?? '',
        email: updated.email ?? '',
        phone: updated.phone ?? '',
        website: updated.website ?? '',
        addressLine1: updated.addressLine1 ?? '',
        addressLine2: updated.addressLine2 ?? '',
        city: updated.city ?? '',
        county: updated.county ?? '',
        postcode: updated.postcode ?? '',
        timezone: updated.timezone ?? 'Europe/London',
        fiscalYearStart: updated.fiscalYearStart ?? '04-06',
      };
      setSaved(next);
      setSavedAt(new Date());
      qc.invalidateQueries({ queryKey: ['me-chambers'] });
      toast.success('Chambers preferences saved');
    } catch {
      toast.error('Failed to save chambers preferences');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Chambers Preferences"
        subtitle="Address, fiscal year, contact details and identification numbers for your chambers"
        onBack={() => navigate(-1)}
      />

      <div className="max-w-2xl space-y-6 pb-24">

        {!isAdmin && (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
            <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Managed by your chambers administrator.</span>
          </div>
        )}

        {/* Identity */}
        <Card className="p-6">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Chambers identity</div>
          <div className="space-y-4">
            <Field label="Chambers name">
              <PInput value={form.name} onChange={set('name')} placeholder="Smith & Co Chambers" disabled={!isAdmin} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Legal name" hint="Full registered legal name if different">
                <PInput value={form.legalName} onChange={set('legalName')} placeholder="Same as chambers name" disabled={!isAdmin} />
              </Field>
              <Field label="Registration no" hint="Companies House or partnership number">
                <PInput value={form.registrationNo} onChange={set('registrationNo')} placeholder="e.g. OC123456" disabled={!isAdmin} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Contact email">
                <PInput value={form.email} onChange={set('email')} placeholder="clerks@chambers.co.uk" type="email" disabled={!isAdmin} />
              </Field>
              <Field label="Phone">
                <PInput value={form.phone} onChange={set('phone')} placeholder="+44 20 …" type="tel" disabled={!isAdmin} />
              </Field>
            </div>
            <Field label="Website">
              <PInput value={form.website} onChange={set('website')} placeholder="https://www.chambers.co.uk" disabled={!isAdmin} />
            </Field>
          </div>
        </Card>

        {/* Address */}
        <Card className="p-6">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registered address</div>
          <div className="space-y-4">
            <Field label="Address line 1">
              <PInput value={form.addressLine1} onChange={set('addressLine1')} placeholder="1 Temple Lane" disabled={!isAdmin} />
            </Field>
            <Field label="Address line 2">
              <PInput value={form.addressLine2} onChange={set('addressLine2')} placeholder="Suite 100" disabled={!isAdmin} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="City">
                <PInput value={form.city} onChange={set('city')} placeholder="London" disabled={!isAdmin} />
              </Field>
              <Field label="County">
                <PInput value={form.county} onChange={set('county')} placeholder="Greater London" disabled={!isAdmin} />
              </Field>
              <Field label="Postcode">
                <PInput value={form.postcode} onChange={set('postcode')} placeholder="EC4Y 7AA" disabled={!isAdmin} />
              </Field>
            </div>
            <Field label="Country">
              <PInput value={chambers?.country ?? 'United Kingdom'} readOnly />
            </Field>
          </div>
        </Card>

        {/* Financial */}
        <Card className="p-6">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Financial</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="VAT number">
              <PInput value={form.vatNumber} onChange={set('vatNumber')} placeholder="GB 123 4567 89" disabled={!isAdmin} />
            </Field>
            <Field label="Currency">
              <PInput value={chambers?.currency ?? 'GBP'} readOnly />
            </Field>
            <Field label="Fiscal year start" hint="Format: MM-DD (e.g. 04-06 = 6 April)">
              <PInput value={form.fiscalYearStart} onChange={set('fiscalYearStart')} placeholder="04-06" disabled={!isAdmin} />
            </Field>
            <Field label="Timezone" hint="Used across the practice for diary, reminders and calendar export">
              <TimezoneSelect value={form.timezone} onChange={set('timezone')} disabled={!isAdmin} />
            </Field>
          </div>
        </Card>

      </div>

      {/* Sticky footer */}
      {isAdmin && (
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-border bg-card/95 backdrop-blur-sm">
          <div className="mx-auto flex max-w-2xl items-center justify-between px-6 py-3">
            <div className="flex items-center gap-2 text-xs">
              {isDirty && (
                <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                  <Warning className="h-3 w-3" weight="fill" />
                  Unsaved changes
                </span>
              )}
              {savedAt && !isDirty && (
                <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle className="h-3 w-3" weight="fill" />
                  Saved {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={!isDirty || saving}
              leftIcon={saving ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <Buildings className="h-3.5 w-3.5" />}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
